// ResponsiveVacationView.jsx
import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab,
  Tooltip,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import itLocale from "date-fns/locale/it";
import { DateTime } from "luxon";
import { auth } from "../../../_services";

const ResponsiveVacationView = ({
  data,
  t,
  jwt,
  onAdd,
  onDelete,
  canAdd,
  showOld = false,
  userData = [],
  showUser = false
  
}) => {
  const userId = auth.getUserId(jwt);
  const isAdmin = auth.getUserRole() !== "User";

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [note, setNote] = useState("");
  const [itemToDelete, setItemToDelete] = useState(null);

  const futureData = data.filter(
    (v) => DateTime.fromISO(v.vacationTo).toMillis() >= DateTime.now().toMillis()
  );
  const displayData = showOld ? data : futureData;

  const groupedByMonth = displayData.reduce((acc, item) => {
    const key = DateTime.fromISO(item.vacationFrom)
      .setLocale("it")
      .toFormat("MMMM yyyy")
      .replace(/^\w/, (c) => c.toUpperCase());
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const handleAdd = () => {
    if (fromDate && toDate && toDate >= fromDate) {
      onAdd({
        id: -1,
        anaUserId: userId,
        vacationFrom: fromDate,
        vacationTo: toDate,
        vacationNote: note,
      });
      setFromDate(null);
      setToDate(null);
      setNote("");
      setAddDialogOpen(false);
    }
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      onDelete(itemToDelete);
      setItemToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Box>
      {canAdd && (
      <Tooltip title={t("addVacation")} placement="top">
        <Fab
          color="primary"
          sx={{ position: "fixed", bottom: 36, left: 16, zIndex: 9999 }}
          onClick={() => setAddDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
      )}
      {Object.entries(groupedByMonth).map(([month, items]) => (
        <Accordion
          key={month}
          sx={{ mb: 2, borderLeft: items.some(i => i.anaUserId === userId) ? "4px solid #1976d2" : "none" }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">{month}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {items.map((vac, idx) => {
              const isMine = vac.anaUserId === userId;
              const userName = userData.find((u) => u.id === vac.anaUserId)?.name || "-";
              const isFuture = DateTime.fromISO(vac.vacationTo).toMillis() >= DateTime.now().toMillis();
              return (
                <Paper
                  key={idx}
                  sx={{
                    p: 2,
                    mb: 1,
                    position: "relative",
                    backgroundColor: isMine ? "#ABEBC6" : "inherit",
                  }}
                  elevation={1}
                >
                  {isFuture && isMine && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setItemToDelete(vac);
                        setDeleteDialogOpen(true);
                      }}
                      sx={{ position: "absolute", top: 4, right: 4 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  {showUser && (
                    <Typography variant="body2">
                      <strong>{t("anaUserId")}: </strong> {userName}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>{t("vacationFrom")}: </strong>
                    {DateTime.fromISO(vac.vacationFrom).toFormat("dd/MM/yyyy")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("vacationTo")}: </strong>
                    {DateTime.fromISO(vac.vacationTo).toFormat("dd/MM/yyyy")}
                  </Typography>
                  {vac.vacationNote && (
                    <Typography variant="body2">
                      <strong>{t("vacationNote")}: </strong> {vac.vacationNote}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}
        fullWidth maxWidth="xs"
        PaperProps={{ sx: { width: 400, maxWidth: '90%' } }}>
        <DialogTitle>{t("addVacation")}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} locale={itLocale}>
            <DatePicker
              label={t("vacationFrom")}
              value={fromDate}
              minDate={new Date()}
              onChange={(newValue) => {
                setFromDate(newValue);
                if (!toDate || newValue > toDate) {
                  setToDate(newValue);
                }
              }}
              renderInput={(params) => <TextField {...params} margin="normal" fullWidth />}
            />
            <DatePicker
              label={t("vacationTo")}
              value={toDate}
              minDate={fromDate || new Date()}
              onChange={(newValue) => setToDate(newValue)}
              renderInput={(params) => <TextField {...params} margin="normal" fullWidth />}
            />
          </LocalizationProvider>
          <TextField
            label={t("vacationNote")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t("cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!fromDate || !toDate || toDate < fromDate}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
        fullWidth maxWidth="xs"
        PaperProps={{ sx: { width: 400, maxWidth: '90%' } }}>
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("cancel")}</Button>
          <Button color="primary" variant="contained" onClick={handleConfirmDelete}>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResponsiveVacationView;
